import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, MapPin, Shield, Wifi, Save } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface RemoteWorkRequest {
  id: string;
  destination_country: string;
  start_date: string;
  end_date: string;
  status: string;
  residence_departure_date?: string;
  destination_arrival_date?: string;
  residence_address_destination?: string;
  insurance_active?: boolean;
  foreign_number?: string;
  vpn_active?: boolean;
  vpn_code?: string;
}

interface TravelDetailsFormProps {
  request: RemoteWorkRequest;
  onUpdate: (updatedRequest: RemoteWorkRequest) => void;
}

const TravelDetailsForm = ({ request, onUpdate }: TravelDetailsFormProps) => {
  const [formData, setFormData] = useState({
    residence_departure_date: request.residence_departure_date ? new Date(request.residence_departure_date) : undefined,
    destination_arrival_date: request.destination_arrival_date ? new Date(request.destination_arrival_date) : undefined,
    residence_address_destination: request.residence_address_destination || '',
    insurance_active: request.insurance_active || false,
    foreign_number: request.foreign_number || '',
    vpn_active: request.vpn_active || false,
    vpn_code: request.vpn_code || ''
  });
  
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSave = async () => {
    setLoading(true);
    try {
      const updateData = {
        residence_departure_date: formData.residence_departure_date?.toISOString().split('T')[0] || null,
        destination_arrival_date: formData.destination_arrival_date?.toISOString().split('T')[0] || null,
        residence_address_destination: formData.residence_address_destination || null,
        insurance_active: formData.insurance_active,
        foreign_number: formData.insurance_active ? formData.foreign_number || null : null,
        vpn_active: formData.vpn_active,
        vpn_code: formData.vpn_active ? formData.vpn_code || null : null
      };

      const { error } = await supabase
        .from('remote_work_requests')
        .update(updateData)
        .eq('id', request.id);

      if (error) throw error;

      // Update the request object with new data
      const updatedRequest = { ...request, ...updateData };
      onUpdate(updatedRequest);

      toast({
        title: "Informations sauvegardées",
        description: "Vos détails de voyage ont été mis à jour avec succès"
      });
    } catch (error) {
      console.error('Error updating travel details:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de sauvegarder les informations"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <MapPin className="h-5 w-5" />
          Détails de voyage
        </CardTitle>
        <CardDescription>
          Complétez les informations de votre séjour en {request.destination_country}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Dates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Date de départ du pays de résidence</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !formData.residence_departure_date && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formData.residence_departure_date ? (
                    format(formData.residence_departure_date, "PPP", { locale: fr })
                  ) : (
                    <span>Sélectionner une date</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={formData.residence_departure_date}
                  onSelect={(date) => setFormData(prev => ({ ...prev, residence_departure_date: date }))}
                  initialFocus
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label>Date d'arrivée dans le pays de destination</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !formData.destination_arrival_date && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formData.destination_arrival_date ? (
                    format(formData.destination_arrival_date, "PPP", { locale: fr })
                  ) : (
                    <span>Sélectionner une date</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={formData.destination_arrival_date}
                  onSelect={(date) => setFormData(prev => ({ ...prev, destination_arrival_date: date }))}
                  initialFocus
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Address */}
        <div className="space-y-2">
          <Label htmlFor="address">Adresse exacte de résidence</Label>
          <Textarea
            id="address"
            placeholder="Entrez l'adresse complète où vous résiderez pendant votre télétravail..."
            value={formData.residence_address_destination}
            onChange={(e) => setFormData(prev => ({ ...prev, residence_address_destination: e.target.value }))}
            rows={3}
          />
        </div>

        {/* Insurance */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-blue-600" />
              <div>
                <Label>Assurance Étranger</Label>
                <p className="text-sm text-muted-foreground">Souscription à une assurance pour l'étranger</p>
              </div>
            </div>
            <Switch
              checked={formData.insurance_active}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, insurance_active: checked }))}
            />
          </div>

          {formData.insurance_active && (
            <div className="ml-8 space-y-2">
              <Label htmlFor="foreign-number">Numéro étranger</Label>
              <Input
                id="foreign-number"
                placeholder="Entrez votre numéro d'assurance étranger"
                value={formData.foreign_number}
                onChange={(e) => setFormData(prev => ({ ...prev, foreign_number: e.target.value }))}
              />
            </div>
          )}
        </div>

        {/* VPN */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Wifi className="h-5 w-5 text-green-600" />
              <div>
                <Label>VPN sécurisé</Label>
                <p className="text-sm text-muted-foreground">Activation du VPN entreprise</p>
              </div>
            </div>
            <Switch
              checked={formData.vpn_active}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, vpn_active: checked }))}
            />
          </div>

          {formData.vpn_active && (
            <div className="ml-8 space-y-2">
              <Label htmlFor="vpn-code">Code VPN entreprise</Label>
              <Input
                id="vpn-code"
                placeholder="Entrez votre code VPN entreprise"
                value={formData.vpn_code}
                onChange={(e) => setFormData(prev => ({ ...prev, vpn_code: e.target.value }))}
              />
            </div>
          )}
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-4">
          <Button onClick={handleSave} disabled={loading} className="w-full md:w-auto">
            <Save className="mr-2 h-4 w-4" />
            {loading ? "Sauvegarde..." : "Sauvegarder les détails"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default TravelDetailsForm;